// Application logger. Public surface: `logger.debug/info/warn/error` for
// quick log calls, `logger.child(scope)` to namespace a chunk of logs.
//
// Behaviour:
//   - Level filter is set from env (`VITE_LOG_LEVEL` overrides the per-env
//     default). 'silent' disables everything.
//   - Context is masked (PII + auth secrets → '[REDACTED]') before any
//     transport sees it.
//   - Transports are registered once from `app/bootstrap/initLogger.ts`. The
//     console transport is always registered; the Sentry transport only after
//     Sentry has been initialised.

import { config } from '@core/config'
import { maskSensitiveFields } from './maskSensitiveFields'
import { consoleTransport } from './transports'
import { getSessionId, getUserId } from './identity'
import { parseOriginFromStack } from './stackTrace'
import {
  LEVEL_PRIORITY,
  type LogContext,
  type LogLevel,
  type LogRecord,
  type Transport,
} from './loggerTypes'

// Service identity is the same on every record — compute once. Sourced from
// the build-time `__APP_NAME__` / `__APP_VERSION__` defines (vite.config.ts
// reads them from package.json), surfaced via `config.app`.
const SERVICE_NAME = config.app.name
const SERVICE_VERSION = config.app.version

let currentMinPriority: number =
  config.logLevel === 'silent' ? Number.POSITIVE_INFINITY : LEVEL_PRIORITY[config.logLevel]
const transports: Transport[] = []

interface DispatchArgs {
  level: LogLevel
  scope: string | undefined
  message: string
  context?: LogContext
  error?: unknown
}

// Identity rides on every record so backend log aggregators / Sentry
// events can be joined on (serviceName, serviceVersion, sessionId) and
// userId (post-login). For error records, the origin frame is parsed from
// the stack so logs can deep-link to source.
const buildEnrichment = (level: LogLevel, error: unknown): LogContext => {
  const enrichment: LogContext = {
    serviceName: SERVICE_NAME,
    serviceVersion: SERVICE_VERSION,
    sessionId: getSessionId(),
  }
  const uid = getUserId()
  if (uid) enrichment.userId = uid

  if (level === 'error' && error instanceof Error) {
    const origin = parseOriginFromStack(error.stack)
    if (origin) enrichment.origin = origin
  }

  return enrichment
}

const dispatch = ({ level, scope, message, context, error }: DispatchArgs) => {
  if (LEVEL_PRIORITY[level] < currentMinPriority) return

  const enrichment = buildEnrichment(level, error)
  const merged: LogContext = context ? { ...enrichment, ...context } : enrichment

  const record: LogRecord = {
    level,
    scope,
    message,
    context: maskSensitiveFields(merged),
    error,
    timestamp: new Date().toISOString(),
  }
  for (const transport of transports) transport.write(record)
}

const make = (scope?: string) => ({
  debug(msg: string, context?: LogContext) {
    dispatch({ level: 'debug', scope, message: msg, context })
  },
  info(msg: string, context?: LogContext) {
    dispatch({ level: 'info', scope, message: msg, context })
  },
  warn(msg: string, context?: LogContext) {
    dispatch({ level: 'warn', scope, message: msg, context })
  },
  error(msg: string, error?: unknown, context?: LogContext) {
    dispatch({ level: 'error', scope, message: msg, context, error })
  },
  child(childScope: string) {
    return make(scope ? `${scope}:${childScope}` : childScope)
  },
})

export const logger = make()
export type AppLogger = ReturnType<typeof make>

// Bootstrap APIs — register transports + adjust level. Not for feature code.

export const registerTransport = (transport: Transport): void => {
  if (transports.some((existing) => existing.name === transport.name)) return
  transports.push(transport)
}

export const setLogLevel = (level: LogLevel | 'silent'): void => {
  currentMinPriority = level === 'silent' ? Number.POSITIVE_INFINITY : LEVEL_PRIORITY[level]
}

// Console transport is registered eagerly so any log call before
// initLogger() still surfaces somewhere. Sentry transport is registered
// from the bootstrap step after Sentry is initialised.
registerTransport(consoleTransport)
